import { type CSSProperties } from 'react';
import { Modal } from '../components/Modal/Modal.js';
import { useTheme } from './ThemeProvider.js';
import { SCHOOL_SPECIFIC_THEME_KEYS } from './themeAccess.js';
import styles from './ThemeStore.module.css';

// Cap the catalog grid columns; the dialog hugs this many so it stays a
// sensible width as the theme catalog grows.
const MAX_CATALOG_COLS = 4;

export interface ThemeStoreProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Browseable catalog of available themes. Designed for scale: today
 * the registry is bundled at build time (~6 themes), but the same
 * picker UX works for dozens of themes via a future server-fed
 * catalog. Selecting a theme switches it immediately and closes the
 * store, mirroring the "tap to pick" pattern from app stores.
 */
export function ThemeStore({ open, onClose }: ThemeStoreProps) {
  const theme = useTheme();
  // `theme.available` already reflects the per-org + per-teacher allowlist
  // (see PhaseRouter allowedThemeKeys), so a private pack like sarah only
  // appears here for the accounts that unlocked it. Just order the result:
  // pin school-specific themes to the end so the catch-all themes lead.
  const items = [...theme.available].sort((a, b) => {
    const aSchool = SCHOOL_SPECIFIC_THEME_KEYS.has(a.key) ? 1 : 0;
    const bSchool = SCHOOL_SPECIFIC_THEME_KEYS.has(b.key) ? 1 : 0;
    return aSchool - bSchool;
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Theme store"
      description="Pick the visual theme for this classroom. More themes coming soon."
      testId="theme-store"
      size="fit"
    >
      <div className={styles.body}>
        <p className={styles.hint}>
          {items.length} theme{items.length === 1 ? '' : 's'} available.
        </p>
        <div
          className={styles.grid}
          style={{ ['--cols']: Math.min(items.length, MAX_CATALOG_COLS) } as CSSProperties}
          role="radiogroup"
          aria-label="Theme"
        >
          {items.map((meta) => {
            const pack = theme.registry.get(meta.key);
            const bg = pack?.getBackground('home');
            const mascot = pack?.getMascot('home');
            const isActive = meta.key === theme.manifest.key;
            return (
              <button
                key={meta.key}
                type="button"
                role="radio"
                aria-checked={isActive}
                onClick={() => {
                  theme.setThemeKey(meta.key);
                  onClose();
                }}
                className={isActive ? `${styles.tile} ${styles.tileActive}` : styles.tile}
                data-testid={`theme-store-tile-${meta.key}`}
              >
                <div
                  className={styles.preview}
                  style={bg ? { backgroundImage: `url(${bg})` } : undefined}
                  aria-hidden="true"
                >
                  {mascot ? (
                    <img className={styles.previewMascot} src={mascot} alt="" draggable={false} />
                  ) : null}
                </div>
                <span className={styles.label}>{meta.name}</span>
                {isActive ? <span className={styles.currentNote}>Current</span> : null}
              </button>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
